import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, Globe } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useT } from '@/lib/i18n/useT';
import { toast } from '@/components/ui/Toast';

/**
 * PRD §7.6 — Two-layer privacy model.
 *
 *   • profilePublic   — teammate-visible name + bio + theme card
 *   • activityPublic  — calendar / activity timeline cross-workspace
 *
 * Defaults are PRIVATE on both columns (entity defaults `false`), so the
 * UI's "private" state is correct on first render even before the user
 * has touched the toggles. Persists via `PUT /profile`.
 */
export function ProfilePrivacySection({
  initial,
}: {
  initial: { profilePublic?: boolean; activityPublic?: boolean };
}) {
  const t = useT();
  const [profilePublic, setProfilePublic] = useState(!!initial.profilePublic);
  const [activityPublic, setActivityPublic] = useState(
    !!initial.activityPublic,
  );

  // Sync from server payload on first hydration / re-fetch.
  useEffect(() => {
    setProfilePublic(!!initial.profilePublic);
    setActivityPublic(!!initial.activityPublic);
  }, [initial.profilePublic, initial.activityPublic]);

  const save = useMutation({
    mutationFn: (patch: { profilePublic?: boolean; activityPublic?: boolean }) =>
      api.put('/profile', patch),
    onSuccess: () => {
      toast({ tone: 'success', title: t('profile.privacy.saved') });
    },
    onError: (e: any) => {
      toast({
        tone: 'error',
        title: t('profile.privacy.saveError'),
        description: e?.message,
      });
    },
  });

  return (
    <div className="mt-4 rounded-lg border border-[var(--color-xk-border)] p-3">
      <h3 className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {t('profile.privacy.eyebrow')}
      </h3>
      <PrivacyRow
        label={t('profile.privacy.profileLabel')}
        hint={t('profile.privacy.profileHint')}
        isPublic={profilePublic}
        onToggle={(v) => {
          setProfilePublic(v);
          save.mutate({ profilePublic: v });
        }}
      />
      <PrivacyRow
        label={t('profile.privacy.activityLabel')}
        hint={t('profile.privacy.activityHint')}
        isPublic={activityPublic}
        onToggle={(v) => {
          setActivityPublic(v);
          save.mutate({ activityPublic: v });
        }}
      />
    </div>
  );
}

function PrivacyRow({
  label,
  hint,
  isPublic,
  onToggle,
}: {
  label: string;
  hint: string;
  isPublic: boolean;
  onToggle: (v: boolean) => void;
}) {
  const t = useT();
  return (
    <label className="mt-3 flex cursor-pointer items-start gap-3 text-sm">
      <button
        type="button"
        role="switch"
        aria-checked={isPublic}
        onClick={() => onToggle(!isPublic)}
        className={`mt-0.5 relative h-5 w-9 rounded-full transition-colors ${
          isPublic
            ? 'bg-[var(--color-xk-accent)]'
            : 'bg-[var(--color-xk-border)]'
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
            isPublic ? 'left-4' : 'left-0.5'
          }`}
        />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-[var(--color-xk-lavender)]">
          {label}
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {isPublic ? (
              <>
                <Globe className="h-3 w-3" /> {t('profile.privacy.public')}
              </>
            ) : (
              <>
                <Lock className="h-3 w-3" /> {t('profile.privacy.private')}
              </>
            )}
          </span>
        </p>
        <p className="mt-0.5 text-[11px] text-[var(--color-xk-text-muted)]">
          {hint}
        </p>
      </div>
    </label>
  );
}
